import React, { useContext, useEffect, useRef, useState } from "react"
import { StyleSheet, Text, View } from "react-native"
import { connect } from "react-redux"
import * as amplitude from "@amplitude/analytics-react-native"
import { theme } from "@common/theme/theme"
import { elide } from "@common/utils/activationSuiteDisplay"
import {
    FUNNEL_TITLE_FONT_SIZE,
    FUNNEL_TITLE_LINE_HEIGHT,
    WIDE_BREAKPOINT,
} from "@common/components/funnel/FunnelQuestionScreen"
import FunnelStepHeader from "@common/components/funnel/FunnelStepHeader"
import FunnelStepLayout, {
    funnelContentWidth,
} from "@common/components/funnel/FunnelStepLayout"
import { FUNNEL_SCENES } from "@common/components/funnel/funnelScenes"
import FunnelChoiceChip from "@common/components/funnel/FunnelChoiceChip"
import {
    PAYMENT_COLLECTION_DAYS,
    formatCollectionDay,
} from "@data/constants/studentsConstants"
import { DimensionsContext } from "@common/utils/mobileUtils"
import { useNewSubscriptionStepper } from "@common/hooks/subscription/useNewSubscriptionStepper"
import {
    gridDisplayOffsetHours,
    resolveFunnelUserTimezone,
} from "@common/student-diagnostic-stepper/constraintsGrid"
import FunnelEmbeddedCheckout from "../brevet/components/FunnelEmbeddedCheckout"
import SummerHelpBanner from "../summer/components/SummerHelpBanner"
import { formatEuros } from "../brevet/utils/formatEuros"
import { FunnelCheckoutFaqSection } from "@common/components/funnel/FunnelCheckoutFaqSection"
import { FunnelRecapLine } from "@common/components/funnel/FunnelCheckoutCards"
import { useInscriptionFunnel } from "./context/InscriptionFunnelContext"
import { useInscriptionFunnelGuard } from "./hooks/useInscriptionFunnelGuard"
import { useInscriptionLead } from "./hooks/useInscriptionLead"
import {
    answersRefusalMessage,
    checkoutRefusalMessage,
    inscriptionFunnelService,
} from "./services/inscriptionFunnelService"
import {
    engagementSavingLabel,
    monthlyPriceLabel,
    quoteIsWithoutCommitment,
    startOptionCards,
    toggleCommitment,
} from "./utils/formulaChoice"
import { formulaGap } from "./utils/subjectsFitHours"
import StartOptionCards from "./components/StartOptionCards"
import {
    INSCRIPTION_STEPS,
    formatSlotsLabel,
    inscriptionFaqFor,
    inscriptionHeadlineGuarantees,
} from "./constants/inscriptionContent"
import {
    ReservationCard,
    ReservationGuarantees,
    ReservationLink,
    ReservationRecap,
    ReservationSetting,
    ReservationTimeline,
} from "./checkout/ReservationBlocks"
import ReservationGuaranteesSheet from "./checkout/ReservationGuaranteesSheet"
import {
    ReservationParentSpace,
    ReservationParentSpaceSheet,
} from "./checkout/ReservationParentSpace"
import {
    commitmentChoices,
    commitmentKey,
    commitmentLabel,
    firstSessionLabel,
    paymentTimeline,
    recapLabel,
} from "./checkout/reservationQuote"

/**
 * Dernière étape — le parent voit ce qu'il réserve, ce qu'il paiera et quand,
 * ce qu'il garde comme liberté, puis paie 1 €.
 *
 * L'ordre du contenu répond à ses questions dans l'ordre où elles se posent :
 *   1. QU'EST-CE QUE j'achète → le format, le volume et la mensualité, sur une
 *      ligne (le détail se déplie : il vient de le composer) ;
 *   2. À QUOI je m'engage, QUAND ça commence, QUEL JOUR on me prélève → trois
 *      réglages, chacun modifiable ici ;
 *   3. COMBIEN et QUAND → la frise des paiements, 1 € aujourd'hui en tête ;
 *   4. ET SI ça ne va pas → trois garanties visibles, les autres dans un volet ;
 *   5. ET APRÈS → ce qui se gère seul depuis l'espace parent.
 *
 * Tous les montants, les dates et l'engagement viennent du DEVIS SERVEUR —
 * l'écran n'en recalcule aucun, et ne dit « sans engagement » que si le devis
 * le dit. La session Stripe est créée à l'arrivée : le formulaire de paiement
 * doit être prêt quand le parent finit de lire.
 *
 * Les trois réglages voyagent DANS la session : en changer un en rouvre une,
 * et c'est le devis revenu avec elle qui s'affiche. Le jour de prélèvement
 * part avec la demande de session ; l'engagement et la date sont des réponses
 * du parcours, écrites d'abord comme à l'étape formule.
 */

const InscriptionCheckoutScreen = ({
    navigation,
    faq,
    userTimezone,
    serverTimezone,
    noCommitment,
    priceGrid,
    weeklyHoursByOffer,
}) => {
    const dimensions = useContext(DimensionsContext)
    const { state, update } = useInscriptionFunnel()
    const { nextParams } = useInscriptionFunnelGuard()
    const { funnelStateId, userToken: leadToken } = useInscriptionLead()
    const { generateDefaultSubjectHoursAllocation } =
        useNewSubscriptionStepper()

    const [clientSecret, setClientSecret] = useState(null)
    const [creatingSession, setCreatingSession] = useState(true)
    const [sessionError, setSessionError] = useState(null)
    // Le jour vient du devis ; `pendingDay` ne tient que le temps que la
    // session rouverte le rende.
    const [pendingDay, setPendingDay] = useState(null)
    // Le réglage ouvert sous sa ligne : "commitment", "start", "day" ou aucun.
    // Ces replis vivent ICI et non dans leurs blocs : rouvrir la session
    // remonte tout le résumé, qui se refermerait sous le doigt du parent.
    const [editor, setEditor] = useState(null)
    const [recapOpen, setRecapOpen] = useState(false)
    const [parentSpaceOpen, setParentSpaceOpen] = useState(false)
    const [guaranteesOpen, setGuaranteesOpen] = useState(false)
    // Seule la DERNIÈRE demande compte : un parent qui enchaîne deux réglages
    // ne doit pas voir la réponse du premier écraser celle du second.
    const sessionRequest = useRef(0)
    const scrollRef = useRef(null)
    // Rouvrir la session remonte le formulaire de paiement, donc le défilement :
    // on repose le parent là où il avait fait son choix.
    const scrollToRestore = useRef(null)

    const windowWidth = dimensions?.width || 400
    // Deux enfants du checkout Stripe sont dimensionnés en dur : ils doivent
    // tenir dans la colonne du gabarit, pas dans la fenêtre.
    const containerWidth = funnelContentWidth(windowWidth)
    const quote = state.quote
    const collectionDay = pendingDay ?? quote?.collection_day ?? 1
    const firstname = state.studentFirstname || "votre enfant"
    const withCommitment = !quoteIsWithoutCommitment(quote)
    // Une session rouverte : l'ancienne reste à l'écran le temps que la
    // nouvelle revienne, et ce qu'elle annonce n'est déjà plus à jour.
    const updating = creatingSession && !!clientSecret

    // Les matières viennent du DEVIS (donc du serveur), pas d'un catalogue
    // rechargé côté client : le récap affiche ce qui est réellement persisté.
    const subjects = quote?.subjects || []

    // Les réponses de « Début des cours » que le devis sert : aucune sur un
    // serveur qui ne les connaît pas.
    const starts = startOptionCards(quote)

    const goToFormula = () =>
        navigation.navigate("inscription/configuration", nextParams)

    // La formule que dit le devis et les règles de l'étape formule : couper
    // l'engagement la remplace par celle que le serveur annonce, le remettre
    // rend la formule d'avant.
    const choice = {
        offerType: quote?.offer_type,
        weeklyHours: quote?.weekly_hours,
        hoursAllocation: state.hoursAllocation,
        withCommitment,
        committedFormula: state.committedFormula,
    }
    const rules = {
        noCommitment,
        weeklyHoursByOffer,
        subjectIds: (state.subjects || []).map(String),
        splitHours: generateDefaultSubjectHoursAllocation,
    }

    // Ce qui manque à ce volume pour s'écrire d'ici : `null`, rien.
    const gapAt = (weeklyHours) =>
        formulaGap({
            studentClass: state.studentClass,
            subjectCount: subjects.length,
            slotCount: (state.slots || []).length,
            weeklyHours,
        })

    const toggleEditor = (name) =>
        setEditor((open) => (open === name ? null : name))

    const rememberScroll = () => {
        scrollToRestore.current =
            scrollRef.current?.getScrollableNode?.()?.scrollTop ?? null
    }

    const openCheckoutSession = (day) => {
        const stateId = funnelStateId
        // Le jeton d'identité du lead : celui de l'URL quand il y est, sinon
        // reconstruit depuis l'email en mémoire. Sans lui, la route refuse.
        const userToken = nextParams?.user || leadToken
        if (!stateId || !userToken) return

        const request = ++sessionRequest.current
        const isLatest = () => request === sessionRequest.current
        // L'ancienne session reste affichée, bouton en attente : le parent ne
        // peut plus la payer, et l'écran ne clignote pas.
        setCreatingSession(true)
        setSessionError(null)

        inscriptionFunnelService
            .createInscriptionCheckoutSession(
                { stateId },
                { payment_collection_day: day },
                { user: userToken }
            )
            .then((res) => {
                if (!isLatest()) return
                const payload = res?.data?.data ?? {}
                setClientSecret(payload.client_secret ?? null)
                // Le devis renvoyé avec la session fait foi : c'est celui qui a
                // été figé au moment de créer le paiement.
                if (payload.quote) update({ quote: payload.quote })
            })
            .catch((err) => {
                if (!isLatest()) return
                const body = err?.response?.data
                // Jamais l'ancienne session sous un réglage qui n'est plus le sien.
                setClientSecret(null)

                // Réservation déjà payée (retour arrière depuis la
                // confirmation) : on ne montre pas d'erreur, on repose le
                // parent sur sa confirmation — il n'a rien à refaire.
                if (body?.error === "already_paid") {
                    update({ paid: true })
                    navigation.reset({
                        index: 0,
                        routes: [
                            {
                                name: "inscription/confirmation",
                                params: nextParams,
                            },
                        ],
                    })
                    return
                }

                // Famille déjà abonnée : ce n'est pas une panne, c'est un refus
                // métier — on le dit tel quel plutôt que « réessayez ».
                setSessionError(
                    checkoutRefusalMessage(
                        err,
                        "Le paiement n'a pas pu être initialisé. Réessayez dans un instant."
                    )
                )
            })
            .finally(() => {
                if (!isLatest()) return
                setCreatingSession(false)
                setPendingDay(null)
            })
    }

    /**
     * Écrit des réponses du parcours (engagement, date) puis rouvre la session
     * qui les porte. Une fois acceptées, le parcours les retient, avec ce que
     * `memory` y ajoute pour lui seul : l'étape formule, si le parent y
     * revient, doit repartir de ce que le serveur sait.
     */
    const changeAnswers = (answers, memory) => {
        const stateId = funnelStateId
        if (!stateId) return

        const request = ++sessionRequest.current
        const isLatest = () => request === sessionRequest.current
        setCreatingSession(true)
        setSessionError(null)
        rememberScroll()

        inscriptionFunnelService
            .updateInscriptionAnswers({ stateId }, answers)
            .then((res) => {
                if (!isLatest()) return
                const nextQuote = res?.data?.data?.quote ?? null
                // Sans devis, la formule n'est pas vendable telle quelle : la
                // session le refusera, avec le message du serveur.
                update({ ...answers, ...memory, quote: nextQuote })
                openCheckoutSession(nextQuote?.collection_day)
            })
            .catch((err) => {
                if (!isLatest()) return
                // Rien n'a été écrit : la session et le devis affichés restent
                // les bons.
                scrollToRestore.current = null
                setCreatingSession(false)
                setPendingDay(null)
                setSessionError(
                    answersRefusalMessage(
                        err,
                        "Votre choix n'a pas pu être enregistré. Réessayez dans un instant."
                    )
                )
            })
    }

    useEffect(() => {
        amplitude.track("inscription_funnel_checkout_viewed", {
            funnel_state_id: funnelStateId,
        })
        // Sans devis encore en main, aucun jour : le serveur garde celui de
        // l'élève plutôt que de le ramener au 1ᵉʳ.
        openCheckoutSession(quote?.collection_day)
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    useEffect(() => {
        if (scrollToRestore.current === null || !clientSecret) return
        const y = scrollToRestore.current
        scrollToRestore.current = null
        requestAnimationFrame(() =>
            scrollRef.current?.scrollTo({ y, animated: false })
        )
    }, [clientSecret])

    const onCollectionDayChange = (day) => {
        // Même jour : rien à rouvrir — sauf si l'ouverture a échoué, où
        // retoucher le jour coché est le « réessayer » du parent.
        setEditor(null)
        if (day === collectionDay && clientSecret) return
        setPendingDay(day)
        rememberScroll()
        openCheckoutSession(day)
    }

    const onCommitmentChoice = (key) => {
        setEditor(null)
        if (key === commitmentKey(quote)) return

        const { committedFormula, ...answers } = toggleCommitment(choice, rules)

        // Quand le nouveau volume ne porte plus toutes les matières — ou que
        // la formule rendue n'en a pas, son format ne le vendant pas —, c'est
        // au compositeur que le parent choisit : on l'y emmène sans rien
        // écrire, la session ouverte ici reste celle du dossier.
        if (gapAt(answers.weeklyHours)) {
            goToFormula()
            return
        }

        amplitude.track("inscription_funnel_commitment_toggled", {
            funnel_state_id: funnelStateId,
            with_commitment: answers.withCommitment,
            source: "checkout",
        })
        changeAnswers(answers, { committedFormula })
    }

    const onStartChoice = (key) => {
        setEditor(null)
        if (key === quote.selected_start_option) return
        const { startDate } = starts.find((option) => option.key === key)
        changeAnswers({ startDate })
    }

    const onPaymentSuccess = () => {
        amplitude.track("inscription_funnel_paid", {
            funnel_state_id: funnelStateId,
        })
        update({ paid: true })
        navigation.navigate("inscription/confirmation", nextParams)
    }

    const onPaymentError = (message) => {
        amplitude.track("inscription_funnel_payment_failed", {
            funnel_state_id: funnelStateId,
        })
        setSessionError(message)
    }

    const summary = (
        <>
            <Text style={styles.title}>
                Réservez la place {elide(firstname)}
            </Text>

            <ReservationCard>
                <ReservationRecap
                    label={quote ? recapLabel(quote) : "Votre formule"}
                    price={quote ? monthlyPriceLabel(quote) : null}
                    open={recapOpen}
                    onToggle={() => setRecapOpen((open) => !open)}
                >
                    {subjects.length > 0 ? (
                        <FunnelRecapLine
                            icon="book"
                            label="Matières"
                            value={subjects.join(", ")}
                        />
                    ) : null}
                    <FunnelRecapLine
                        icon="calendar-week"
                        label="Créneaux"
                        value={formatSlotsLabel(
                            state.slots || [],
                            gridDisplayOffsetHours(userTimezone, serverTimezone)
                        )}
                    />
                    <ReservationLink
                        label="Modifier la formule"
                        onPress={goToFormula}
                    />
                </ReservationRecap>

                {/* Aucun montant en dur : sans devis serveur, ni réglages ni
                    frise — l'écran n'en suppose pas. */}
                {quote ? (
                    <View
                        style={[styles.cardBody, updating && styles.updating]}
                    >
                        <ReservationSetting
                            label="Engagement"
                            value={commitmentLabel(quote)}
                            // Sans formule annoncée par le serveur, le funnel
                            // ne vend qu'engagé : il n'y a rien à choisir.
                            onEdit={
                                noCommitment
                                    ? () => toggleEditor("commitment")
                                    : null
                            }
                        >
                            {editor === "commitment" && noCommitment ? (
                                <View style={styles.editor}>
                                    <StartOptionCards
                                        groupLabel="Engagement"
                                        options={commitmentChoices(
                                            noCommitment,
                                            gapAt(noCommitment.weekly_hours),
                                            engagementSavingLabel(
                                                priceGrid,
                                                choice,
                                                rules
                                            )
                                        )}
                                        selectedKey={commitmentKey(quote)}
                                        onSelect={onCommitmentChoice}
                                    />
                                </View>
                            ) : null}
                        </ReservationSetting>

                        <ReservationSetting
                            label="Début des cours"
                            value={firstSessionLabel(quote)}
                            // Un devis sans les deux cartes : la date se
                            // choisit au compositeur, comme avant.
                            onEdit={
                                starts.length > 0
                                    ? () => toggleEditor("start")
                                    : goToFormula
                            }
                        >
                            {editor === "start" ? (
                                <View style={styles.editor}>
                                    <StartOptionCards
                                        options={starts}
                                        selectedKey={
                                            quote.selected_start_option
                                        }
                                        onSelect={onStartChoice}
                                    />
                                    <ReservationLink
                                        label="Une autre date"
                                        onPress={goToFormula}
                                    />
                                </View>
                            ) : null}
                        </ReservationSetting>

                        <ReservationSetting
                            label="Prélèvement mensuel"
                            value={`Le ${formatCollectionDay(collectionDay)}`}
                            onEdit={() => toggleEditor("day")}
                        >
                            {editor === "day" ? (
                                <View style={[styles.editor, styles.dayPicker]}>
                                    {PAYMENT_COLLECTION_DAYS.map((day) => (
                                        <FunnelChoiceChip
                                            key={day}
                                            label={formatCollectionDay(day)}
                                            selected={day === collectionDay}
                                            onPress={() =>
                                                onCollectionDayChange(day)
                                            }
                                        />
                                    ))}
                                </View>
                            ) : null}
                        </ReservationSetting>

                        <ReservationTimeline
                            points={paymentTimeline(quote, collectionDay)}
                        />
                    </View>
                ) : null}
            </ReservationCard>

            <ReservationGuarantees
                items={inscriptionHeadlineGuarantees(withCommitment)}
                onPress={() => setGuaranteesOpen(true)}
            />
            <ReservationParentSpace onPress={() => setParentSpaceOpen(true)} />
        </>
    )

    // Même bloc sous le paiement et sous son fallback : une seule définition,
    // sinon les deux chemins dérivent (c'est déjà arrivé côté été).
    const belowCheckout = (
        <>
            <FunnelCheckoutFaqSection
                faqs={inscriptionFaqFor(faq, withCommitment)}
            />
            <SummerHelpBanner
                step={INSCRIPTION_STEPS.checkout}
                trackingEvent="inscription_funnel_help_clicked"
            />
        </>
    )

    return (
        <FunnelStepLayout
            // Rien au-dessus du titre sur un téléphone : la scène n'occupe que
            // le panneau de gauche des grands écrans, qui resterait vide.
            scene={
                windowWidth >= WIDE_BREAKPOINT ? FUNNEL_SCENES.checkout : null
            }
            scroll={false}
            header={
                <FunnelStepHeader
                    step={INSCRIPTION_STEPS.checkout}
                    total={INSCRIPTION_STEPS.total}
                    onBack={() => navigation.goBack()}
                />
            }
            overlay={
                <>
                    <ReservationGuaranteesSheet
                        isVisible={guaranteesOpen}
                        onClose={() => setGuaranteesOpen(false)}
                        withCommitment={withCommitment}
                    />
                    <ReservationParentSpaceSheet
                        isVisible={parentSpaceOpen}
                        onClose={() => setParentSpaceOpen(false)}
                    />
                </>
            }
        >
            {({ scrollProps, spacer }) => (
                <FunnelEmbeddedCheckout
                    clientSecret={clientSecret}
                    scrollProps={scrollProps}
                    scrollRef={scrollRef}
                    summary={
                        <>
                            {spacer}
                            {summary}
                        </>
                    }
                    // Un seul bouton fort, et rien dessous que les CGU : la
                    // réassurance est au-dessus, dans les garanties. Le
                    // libellé passe à la ligne sur un téléphone : les espaces
                    // insécables gardent le montant avec le prénom, plutôt
                    // qu'un « · 1 € » seul sur la sienne.
                    buttonLabel={
                        quote
                            ? `Réserver la place ${elide(firstname)}\u00a0·\u00a0${formatEuros(quote.deposit_cents / 100)}`
                            : `Réserver la place ${elide(firstname)}`
                    }
                    guarantees={[]}
                    secureBadge
                    belowContent={belowCheckout}
                    fallbackBelowContent={belowCheckout}
                    onSuccess={onPaymentSuccess}
                    onError={onPaymentError}
                    creatingSession={creatingSession}
                    sessionError={sessionError}
                    containerWidth={containerWidth}
                    nativeNotice="Le paiement se finalise depuis un navigateur. Rouvrez le lien sur un ordinateur pour terminer votre réservation."
                />
            )}
        </FunnelStepLayout>
    )
}

const styles = StyleSheet.create({
    title: {
        fontFamily: "Montserrat-ExtraBold",
        fontSize: FUNNEL_TITLE_FONT_SIZE,
        lineHeight: FUNNEL_TITLE_LINE_HEIGHT,
        color: theme.colors.primaryDark || theme.colors.primary,
        marginBottom: 16,
    },
    // Estompé ET sourd : un second choix pendant que la session se rouvre en
    // laisserait une de trop chez Stripe.
    updating: { opacity: 0.5, pointerEvents: "none" },
    cardBody: { paddingHorizontal: 16, paddingBottom: 14 },
    // Le choix ouvert sous la ligne de son réglage.
    editor: { marginTop: 6, marginBottom: 10, gap: 8 },
    dayPicker: { flexDirection: "row", flexWrap: "wrap", gap: 6 },
})

const mapStateToProps = (state) => ({
    faq: state.data?.inscriptionFunnel?.faq,
    // La seule formule vendue sans engagement, annoncée par le serveur.
    // Absente, l'engagement ne se change pas ici.
    noCommitment: state.data?.inscriptionFunnel?.noCommitment,
    priceGrid: state.data?.inscriptionFunnel?.priceGrid,
    weeklyHoursByOffer: state.data?.inscriptionFunnel?.weeklyHoursByOffer,
    // Mêmes fuseaux que la grille du compositeur : ce récapitulatif dit les
    // créneaux que le parent vient d'y cocher, donc il doit les dire à la même
    // heure. Sans eux il annonçait Paris sous une case étiquetée en local.
    //
    // Cf. resolveFunnelUserTimezone (repli sur l'appareil à la lecture).
    userTimezone: resolveFunnelUserTimezone(
        state.data?.inscriptionFunnel?.userTimezone
    ),
    serverTimezone: state.data?.inscriptionFunnel?.serverTimezone,
})

export default connect(mapStateToProps)(InscriptionCheckoutScreen)
